import { Link, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { ChartMultipleRegular, PeopleRegular } from "@fluentui/react-icons";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { Fragment } from "react";
import { useValueStoryPageStyles } from "./ValueStoryPage.styles";

export const ValueStoryPage = (): JSX.Element => {
  const styles = useValueStoryPageStyles();
  const { links: valueStoryLinks, isLoading: isLinksLoading } =
    useLinks("value_story");

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <p className={styles.intro}>
          As the engagement moves toward closure, revisit the value measures
          defined during mobilization. Use this page to confirm that outcomes
          were achieved, quantify the impact delivered, and craft a clear
          narrative you can share with the client and North Highland
          stakeholders.
        </p>

        <div className={styles.cardRow}>
          <div className={styles.card}>
            <div className={styles.cardIcon} aria-hidden="true">
              <ChartMultipleRegular fontSize={18} />
            </div>
            <h2 className={styles.cardTitle}>Revisit your value measures</h2>
            <p className={styles.cardBody}>
              Return to the KPIs, success metrics, and outcome statements agreed
              at mobilization. For each measure, assess what was achieved and
              gather supporting data.
            </p>
            <ul className={styles.cardList}>
              <li className={styles.cardListItem}>
                Pull the original value framework from the mobilization workbook
                or SOW
              </li>
              <li className={styles.cardListItem}>
                Compare baseline figures to end-of-engagement actuals
              </li>
              <li className={styles.cardListItem}>
                Note any measures that shifted in scope and document why
              </li>
              <li className={styles.cardListItem}>
                Gather client-sourced data to validate outcomes where possible
              </li>
            </ul>
          </div>

          <div className={styles.card}>
            <div className={styles.cardIcon} aria-hidden="true">
              <PeopleRegular fontSize={18} />
            </div>
            <h2 className={styles.cardTitle}>Tell the story</h2>
            <p className={styles.cardBody}>
              Structure the value narrative so it resonates with the client and
              executive stakeholders — what was promised, what was delivered,
              and the measurable difference it made.
            </p>
            <ul className={styles.cardList}>
              <li className={styles.cardListItem}>
                Lead with the business outcome, not the workstream activity
              </li>
              <li className={styles.cardListItem}>
                Anchor every claim to a specific, quantified result
              </li>
              <li className={styles.cardListItem}>
                Acknowledge scope changes honestly and frame them as decisions
              </li>
              <li className={styles.cardListItem}>
                Close with forward-looking impact — what the client can do next
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.resourcesRow}>
          <span className={styles.resourcesLabel}>Resources</span>
          {isLinksLoading ? (
            <Skeleton>
              <SkeletonItem
                animation="wave"
                className={styles.skeletonResourceLink}
              />
            </Skeleton>
          ) : valueStoryLinks.length === 0 ? (
            <>
              <span className={styles.resourceDivider}>·</span>
              <span className={styles.resourcesEmpty}>
                No resources configured yet
              </span>
            </>
          ) : (
            valueStoryLinks.map((link) => (
              <Fragment key={link.id}>
                <span className={styles.resourceDivider}>·</span>
                <Link
                  href={link.url}
                  target="_blank"
                  className={styles.resourceLink}
                >
                  {link.displayName}
                </Link>
              </Fragment>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
